/**
 * The dialogs after the edit, in document order: a reviewer's return of a
 * submission, and the three that change the task's record rather than its
 * state — deletion, prerequisites, and a GitHub reference.
 */

import type { ReactNode } from 'react';

import type { ApiFailure, TaskView } from '../../api.js';
import { DependencyPicker, useWorkspaceCopy } from '../../components.js';
import { ChoiceGroup, ConfirmDialog, TextField } from '../../ui.js';
import type { TaskDialogFields } from './useTaskDialogFields.js';
import type { DialogKind } from './useTaskDialogs.js';

export function TaskRecordDialogs({
  view,
  dialog,
  busy,
  actionFailure,
  failureAlert,
  closeDialog,
  act,
  fields,
  loadDependencyCandidates,
}: {
  readonly view: TaskView;
  readonly dialog: DialogKind;
  readonly busy: boolean;
  readonly actionFailure: ApiFailure | null;
  readonly failureAlert: ReactNode;
  readonly closeDialog: () => void;
  readonly act: (view: TaskView, kind: Exclude<DialogKind, null>) => void;
  readonly fields: TaskDialogFields;
  readonly loadDependencyCandidates: (selfTaskId: string) => void;
}): ReactNode {
  const copy = useWorkspaceCopy();
  const task = view.task;
  const {
    reason,
    setReason,
    referenceKind,
    setReferenceKind,
    referenceUrl,
    setReferenceUrl,
    dependencyIds,
    setDependencyIds,
    dependencyCandidates,
  } = fields;

  return (
    <>
      <ConfirmDialog
        open={dialog === 'return'}
        title={copy.dialogs.returnTitle}
        body={copy.dialogs.returnBody}
        whatHappensLabel={copy.dialogs.whatHappens}
        effects={copy.dialogs.returnEffects}
        confirmLabel={copy.actions.returnToProgress}
        cancelLabel={copy.actions.cancel}
        busy={busy}
        error={failureAlert}
        onCancel={closeDialog}
        onConfirm={() => {
          act(view, 'return');
        }}
      >
        <TextField
          label={copy.dialogs.reasonLabel}
          value={reason}
          onChange={setReason}
          error={
            reason.trim().length === 0 && actionFailure?.kind === 'invalid'
              ? copy.dialogs.reasonRequired
              : null
          }
        />
      </ConfirmDialog>

      <ConfirmDialog
        open={dialog === 'delete'}
        title={copy.deletion.title}
        body={copy.deletion.body(task.title)}
        whatHappensLabel={copy.dialogs.whatHappens}
        effects={copy.deletion.effects}
        confirmLabel={copy.deletion.action}
        cancelLabel={copy.actions.cancel}
        danger
        busy={busy}
        error={failureAlert}
        onCancel={closeDialog}
        onConfirm={() => {
          act(view, 'delete');
        }}
      />

      <ConfirmDialog
        open={dialog === 'dependencies'}
        title={copy.execution.editDependencies}
        body={copy.execution.editDependenciesBody}
        whatHappensLabel={copy.dialogs.whatHappens}
        effects={copy.execution.editDependenciesEffects}
        confirmLabel={copy.actions.save}
        cancelLabel={copy.actions.cancel}
        busy={busy}
        error={failureAlert}
        onCancel={closeDialog}
        onConfirm={() => {
          act(view, 'dependencies');
        }}
      >
        <DependencyPicker
          legend={copy.execution.dependencies}
          hint={copy.propose.prerequisitesHint}
          candidates={dependencyCandidates}
          selected={dependencyIds}
          onToggle={(taskId, wanted) => {
            setDependencyIds((current) =>
              wanted
                ? current.includes(taskId)
                  ? current
                  : [...current, taskId]
                : current.filter((id) => id !== taskId),
            );
          }}
          onRetry={() => {
            loadDependencyCandidates(task.id);
          }}
        />
      </ConfirmDialog>

      <ConfirmDialog
        open={dialog === 'reference'}
        title={copy.task.addReference}
        body={copy.task.githubUnverified}
        whatHappensLabel={copy.dialogs.whatHappens}
        effects={[copy.toasts.referenceAdded]}
        confirmLabel={copy.actions.save}
        cancelLabel={copy.actions.cancel}
        busy={busy}
        error={failureAlert}
        onCancel={closeDialog}
        onConfirm={() => {
          act(view, 'reference');
        }}
      >
        <ChoiceGroup
          legend={copy.task.referenceKind}
          value={referenceKind}
          onChange={setReferenceKind}
          inline
          choices={['ISSUE', 'PULL_REQUEST', 'MERGE'].map((value) => ({
            value,
            label: copy.task.referenceKinds[value] ?? value,
          }))}
        />
        <TextField
          label={copy.task.referenceUrl}
          value={referenceUrl}
          onChange={setReferenceUrl}
          dir="ltr"
          type="url"
          placeholder="https://github.com/…"
        />
      </ConfirmDialog>
    </>
  );
}
